import type { ExplorationAccountCache } from '../types/exploration';

export interface ExplorationAccountOption {
  id: string;
  label: string;
  indent?: boolean;
}

export function formatAccountOptionLabel(name: string, volumeCount: number, indent = false): string {
  const base = `${name} (${volumeCount.toLocaleString()})`;
  return indent ? `↳ ${base}` : base;
}

function resolveOrgChildIds(
  caches: Record<string, ExplorationAccountCache>,
  orgKey: string,
  orgCache: ExplorationAccountCache
): string[] {
  if (orgCache._orgChildIds?.length) return orgCache._orgChildIds;

  const parentId = orgKey.slice(4);
  if (!parentId) return [];

  return Object.entries(caches)
    .filter(
      ([id, acct]) =>
        id !== orgKey && !id.startsWith('org:') && acct._orgParentId === parentId
    )
    .map(([id]) => id);
}

export function buildAccountOptions(
  caches: Record<string, ExplorationAccountCache>
): ExplorationAccountOption[] {
  const orgKeys = Object.keys(caches).filter((k) => k.startsWith('org:'));
  if (!orgKeys.length) {
    return Object.entries(caches).map(([id, acct]) => ({
      id,
      label: formatAccountOptionLabel(acct.name, acct.volumes.length),
    }));
  }

  const options: ExplorationAccountOption[] = [];
  const listed = new Set<string>();

  for (const orgKey of orgKeys) {
    const cache = caches[orgKey];
    const childIds = resolveOrgChildIds(caches, orgKey, cache);

    if (childIds.length > 0) {
      options.push({
        id: orgKey,
        label: formatAccountOptionLabel(cache.name, cache.volumes.length),
      });
      listed.add(orgKey);
      const parentId = orgKey.slice(4);
      if (parentId) listed.add(parentId);

      for (const childId of childIds) {
        const childCache = caches[childId];
        options.push({
          id: childId,
          label: formatAccountOptionLabel(
            childCache?.name || childId,
            childCache?.volumes.length ?? 0,
            true
          ),
          indent: true,
        });
        listed.add(childId);
      }
    } else if (!cache._isVirtualOrg) {
      const parentId = orgKey.slice(4);
      const parentCache = caches[parentId];
      if (parentCache) {
        options.push({
          id: parentId,
          label: formatAccountOptionLabel(parentCache.name, parentCache.volumes.length),
        });
        listed.add(parentId);
      }
    }
  }

  for (const [id, acct] of Object.entries(caches)) {
    if (listed.has(id) || id.startsWith('org:') || acct._orgParentId) continue;
    options.push({ id, label: formatAccountOptionLabel(acct.name, acct.volumes.length) });
  }

  return options;
}
